import { isBefore, isSameDay, add } from 'date-fns'
import { ConstrainedString } from '../generic'
import { MarkdownString } from '../MarkdownString'
import { Timestamp, timestampToDate } from '../Timestamp'
import { InvestorVoteResponse } from '../Ballot/Ballot'
import { PollProposalRelationships } from './Proposal'
import { Tally } from './Tally'
import { Tag } from './Tag'

export type PollId = ConstrainedString<'poll-id'>

export interface Poll {
  id: PollId
  title: string
  formattedDescription: MarkdownString
  publishAt: Timestamp
  tags: Tag[]
  teaserText?: string
  teaserImage?: Image
  isHotTopic: boolean
  endDate: Timestamp
  tally: Tally
  isNewToPlatform: boolean
  relationships: PollProposalRelationships
}

export interface Image {
  url: string
  altText: string
}

export const isPollOpen = (poll: Poll): boolean => {
  const now = new Date()
  return isBefore(now, timestampToDate(poll.endDate))
}

export const isPollEndingSoon = (poll: Poll): boolean => {
  const sixDaysFromNow = add(new Date(), { days: 6 })
  return isBefore(timestampToDate(poll.endDate), sixDaysFromNow)
}

export type PollTallyOutcome = InvestorVoteResponse | 'no votes' | 'no outcome' | 'tied'

export const calculatePollTallyOutcome = (poll: Poll): PollTallyOutcome => {
  const now = new Date()
  const maxValue = Math.max(
    poll.tally.forCount,
    poll.tally.againstCount,
    poll.tally.abstainCount,
    poll.tally.withholdCount,
    poll.tally.noActionCount
  )
  const countsWithMaxValue = [
    poll.tally.forCount,
    poll.tally.againstCount,
    poll.tally.abstainCount,
    poll.tally.withholdCount,
    poll.tally.noActionCount,
  ].filter((count) => count === maxValue)
  if (isBefore(now, timestampToDate(poll.endDate)) || isSameDay(now, timestampToDate(poll.endDate))) {
    return 'no outcome'
  }
  if (countsWithMaxValue.length > 1) {
    return 'tied'
  }
  if (poll.tally.forCount + poll.tally.againstCount === 0) {
    return 'no votes'
  }
  if (poll.tally.forCount === maxValue) {
    return 'for'
  }
  if (poll.tally.againstCount === maxValue) {
    return 'against'
  }
  if (poll.tally.abstainCount === maxValue) {
    return 'abstain'
  }
  if (poll.tally.withholdCount === maxValue) {
    return 'withhold'
  }
  if (poll.tally.noActionCount === maxValue) {
    return 'no_action'
  }
  return 'no outcome'
}
